import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import ProductLandingCheckoutPage from '@/components/ProductLandingCheckoutPage';
import SeoHead from '@/components/SeoHead';
import { fetchProductsAdmin } from '@/lib/firebaseAdmin';
import { isProductUnavailable } from '@/lib/productAvailability';
import { getProductDescriptionSummary } from '@/lib/productDescription';
import {
  buildProductLandingPath,
  buildProductLookup,
  findProductVariant,
  formatProductCategory,
  getProductVariantSlug,
} from '@/lib/productRoutes';
import { getAbsoluteSocialImageUrl, getAbsoluteUrl, resolveImageValue } from '@/lib/seo';
import { getProductPricing } from '@/lib/productPricing';

const getProductTimestamp = (product) => Number(product?.updatedAt || product?.createdAt || 0);

const normalizeMetaText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const truncateMetaTitle = (value = '', limit = 60) => {
  const normalizedValue = normalizeMetaText(value);
  if (!normalizedValue) return '';
  if (normalizedValue.length <= limit) return normalizedValue;
  return `${normalizedValue.slice(0, Math.max(limit - 3, 0)).trimEnd()}...`;
};

const buildSocialDescription = (value = '', fallback = '') => {
  const normalizedValue = normalizeMetaText(value || fallback);
  if (!normalizedValue) return '';

  const paddedValue =
    normalizedValue.length < 90
      ? `${normalizedValue} Shop stylish footwear from Trendy Quest Collection in Kenya.`
      : normalizedValue;

  if (paddedValue.length <= 155) return paddedValue;
  return `${paddedValue.slice(0, 152).trimEnd()}...`;
};

const getLandingEntries = (products = []) =>
  products.flatMap((product) => {
    const entries = [
      {
        product,
        selectedColorSlug: '',
        path: buildProductLandingPath(product),
      },
    ];

    (product.variants || []).forEach((variant) => {
      const selectedColorSlug = getProductVariantSlug(variant);
      const path = buildProductLandingPath(product, { color: variant?.name });

      if (selectedColorSlug && path) {
        entries.push({
          product,
          selectedColorSlug,
          path,
        });
      }
    });

    return entries;
  });

const getRecommendedProducts = (products = [], product) =>
  products
    .filter((item) => item.id !== product.id)
    .sort((left, right) => {
      const leftScore =
        (left.subcategorySlug === product.subcategorySlug ? 3 : 0) +
        (left.departmentSlug === product.departmentSlug ? 1 : 0);
      const rightScore =
        (right.subcategorySlug === product.subcategorySlug ? 3 : 0) +
        (right.departmentSlug === product.departmentSlug ? 1 : 0);

      if (leftScore !== rightScore) return rightScore - leftScore;
      return getProductTimestamp(right) - getProductTimestamp(left);
    })
    .slice(0, 4);

export default function ProductLandingPage({
  product,
  recommendedProducts = [],
  selectedColorSlug = '',
}) {
  const router = useRouter();
  const [urlColorSlug, setUrlColorSlug] = useState('');

  useEffect(() => {
    if (!router.isReady || selectedColorSlug || typeof window === 'undefined') return;

    const basePath = buildProductLandingPath(product);
    const pathname = window.location.pathname.replace(/\/$/, '');
    const variantPathPrefix = `${basePath}/`;

    if (!pathname.startsWith(variantPathPrefix)) {
      setUrlColorSlug('');
      return;
    }

    const variantPath = pathname.slice(variantPathPrefix.length);
    const variant = variantPath.includes('/') ? null : findProductVariant(product, variantPath);
    setUrlColorSlug(variant ? getProductVariantSlug(variant) : '');
  }, [product, router.asPath, router.isReady, selectedColorSlug]);

  const resolvedColorSlug = selectedColorSlug || urlColorSlug;
  const resolvedVariant = findProductVariant(product, resolvedColorSlug) || product?.variants?.[0] || null;
  const resolvedColorName = String(resolvedVariant?.name || '').trim();
  const currentImage = resolveImageValue(
    resolvedVariant?.image || product?.images?.[0] || product?.image || ''
  );
  const socialImageUrl = getAbsoluteSocialImageUrl(currentImage);
  const descriptionSummary = getProductDescriptionSummary(product?.description, product?.name);
  const canonicalPath = resolvedColorSlug
    ? buildProductLandingPath(product, { color: resolvedColorName })
    : buildProductLandingPath(product);
  const pageTitle = `${product.name}${resolvedColorName ? ` - ${resolvedColorName}` : ''} Direct Order | Trendy Quest`;
  const socialTitle = truncateMetaTitle(pageTitle);
  const pageDescription =
    descriptionSummary ||
    `Order ${product.name}${resolvedColorName ? ` in ${resolvedColorName}` : ''} from Trendy Quest in Kenya.`;
  const socialDescription = buildSocialDescription(pageDescription);
  
  // Build OG description with price and delivery info
  const { salePrice, regularPrice } = getProductPricing(product);
  const formattedPrice = salePrice ? `KES ${Number(salePrice).toLocaleString('en-KE')}` : '';
  const openGraphDescription = formattedPrice
    ? buildSocialDescription(
        `${formattedPrice} | Free delivery in Nairobi CBD, countrywide delivery available. Order ${product.name}${resolvedColorName ? ` in ${resolvedColorName}` : ''} from Trendy Quest in Kenya.`,
        pageDescription
      )
    : socialDescription;

  return (
    <>
      <SeoHead
        title={socialTitle}
        openGraphTitle={socialTitle}
        twitterTitle={socialTitle}
        description={openGraphDescription}
        openGraphDescription={openGraphDescription}
        twitterDescription={openGraphDescription}
        path={canonicalPath}
        image={socialImageUrl || currentImage}
        imageWidth={1200}
        imageHeight={630}
        imageAlt={product.name}
        type="product"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: pageDescription,
          image: [socialImageUrl].filter(Boolean),
          sku: product.sku || product.id,
          category: formatProductCategory(product),
          ...(resolvedColorName ? { color: resolvedColorName } : {}),
          brand: {
            '@type': 'Brand',
            name: 'Trendy Quest Collection',
          },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'KES',
            price: salePrice || regularPrice,
            itemCondition: 'https://schema.org/NewCondition',
            availability: !isProductUnavailable(product)
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
            eligibleRegion: 'KE',
            url: getAbsoluteUrl(canonicalPath) || canonicalPath,
          },
        }}
      />
      <ProductLandingCheckoutPage
        product={product}
        recommendedProducts={recommendedProducts}
        selectedColorSlug={resolvedColorSlug}
      />
    </>
  );
}

ProductLandingPage.propTypes = {
  product: PropTypes.object.isRequired,
  recommendedProducts: PropTypes.arrayOf(PropTypes.object),
  selectedColorSlug: PropTypes.string,
};

export async function getStaticPaths() {
  const { products } = buildProductLookup(await fetchProductsAdmin());

  return {
    paths: getLandingEntries(products).map((entry) => ({
      params: {
        productPath: entry.path.split('/').filter(Boolean).slice(1),
      },
    })),
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }) {
  const { products } = buildProductLookup(await fetchProductsAdmin());
  const requestedPath = `/landing/${(params?.productPath || []).join('/')}`;
  const landingEntry = getLandingEntries(products).find((entry) => entry.path === requestedPath);

  if (!landingEntry) {
    return {
      notFound: true,
      revalidate: 300,
    };
  }

  const canonicalPath = landingEntry.path;

  if (requestedPath !== canonicalPath) {
    return {
      redirect: {
        destination: canonicalPath,
        permanent: true,
      },
      revalidate: 300,
    };
  }

  return {
    props: {
      product: landingEntry.product,
      recommendedProducts: getRecommendedProducts(products, landingEntry.product),
      selectedColorSlug: landingEntry.selectedColorSlug,
    },
    revalidate: 300,
  };
}
